<script setup lang="ts">
import type { ResolvedDisplayTemplateRenderNode } from '#layers/data-model/public';
import { ref, useTemplateRef } from 'vue';
import { truncatedTextTooltipUi, useTruncation } from '#layers/ui/public';
import RenderFieldDisplay from './RenderFieldDisplay.vue';

defineOptions({ inheritAttrs: false });

const { nodes } = defineProps<{
	nodes: ResolvedDisplayTemplateRenderNode[];
}>();

const contentRef = useTemplateRef<HTMLElement>('content');
const { measure } = useTruncation(contentRef);

const open = ref(false);
</script>

<!--
	Measures only when the tooltip asks to open, so templates nobody lingers on cost no layout reads.
	Field displays carry their own links, tooltips, hover cards and dropdowns, which mustn't react inside a tooltip. Their
	triggers (Reka marks each with `data-state`) ignore the pointer instead of the whole copy going `inert`, which would
	also stop the text from being selected.
-->
<template>
	<DTooltip :open :ui="truncatedTextTooltipUi" @update:open="(value) => open = value && measure()">
		<div ref="content" v-bind="$attrs">
			<slot />
		</div>

		<template #content>
			<!-- `v-text`: a formatter-wrapped `{{ node.text }}` would add visible whitespace under `pre-wrap`. -->
			<div class="min-w-0 wrap-break-word whitespace-pre-wrap [&_:is(a,button,[data-state])]:pointer-events-none">
				<template v-for="(node, index) in nodes" :key="index">
					<span v-if="'text' in node" v-text="node.text" />
					<!--
						Field displays flow inline so they wrap instead of clipping again. Boxes with media, icons or
						swatches (empty elements) stay atomic, and wrapping boxes repeat their padding and background per line.
					-->
					<span
						v-else
						class="
							**:box-decoration-clone **:whitespace-normal
							[&_*:has(img,svg,:empty)]:inline-flex [&_*:has(img,svg,:empty)]:align-middle
							[&_*:not(:empty,:has(img,svg,:empty))]:inline
						"
					>
						<RenderFieldDisplay :field-id="node.fieldId" :value="node.value" :item="node.leafItem" :root="false" />
					</span>
				</template>
			</div>
		</template>
	</DTooltip>
</template>
